import { CopyOutlined, DoubleLeftOutlined, DoubleRightOutlined, PauseOutlined } from '@ant-design/icons';
import { Switch } from 'antd';
import { AdGroupBean, ListResponseAdGroupListItemBean } from 'common/api/api';
import { find } from 'common/api/entities';
import { adGroupApi } from 'common/api/entitiesApi';
import { ActiveStatusSelector } from 'common/components/dictionary/ActiveStatusSelector';
import { GoToReportButton } from 'common/components/GoToReportButton.tsx';
import { ListActionButton } from 'common/components/ListActionButton.tsx';
import { ListCard } from 'common/components/ListCard';
import { AdvertiserSelector } from 'common/components/reloadable/AdvertiserSelector';
import { CampaignSelector } from 'common/components/reloadable/CampaignSelector';
import { CreativeSelector } from 'common/components/reloadable/CreativeSelector';
import { RetargetingListSelector } from 'common/components/reloadable/RetargetingListSelector';
import { SearchInput } from 'common/components/SearchInput';
import { ListFilters, useAppContext } from 'common/context/AppContext';
import { CommonRoutes } from 'common/context/commonRoutes';
import { COLUMN_ID_WIDTH, COLUMN_NAME_WIDTH } from 'common/util/constants';
import { initListFilters, useListFilters } from 'common/util/useListFilters';
import { apiCall, Format, formatCompact } from 'common/util/Util';
import dayjs from 'dayjs';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const priorityColors: Record<AdGroupBean.PriorityEnum, string> = {
  [AdGroupBean.PriorityEnum.High]: '#334D97',
  [AdGroupBean.PriorityEnum.Medium]: '#5C71AC',
  [AdGroupBean.PriorityEnum.Low]: '#5888CF',
}

const renderPriorityIcon = (priority: AdGroupBean.PriorityEnum) => {
  switch (priority) {
    case AdGroupBean.PriorityEnum.High:
      return <DoubleLeftOutlined rotate={90} />
    case AdGroupBean.PriorityEnum.Medium:
      return <PauseOutlined rotate={90} />
    case AdGroupBean.PriorityEnum.Low:
      return <DoubleRightOutlined rotate={90} />
  }
}

const AdGroupList = ({agency}: { agency: boolean }) => {
    const navigateTo = useNavigate()
    const appContext = useAppContext()
    const [filters, setFilters] = useState<ListFilters>(initListFilters())
    const {setFilter} = useListFilters(setFilters)
    const [loading, setLoading] = useState<boolean>(false)
    const [listData, setListData] = useState<ListResponseAdGroupListItemBean>()

    const loadData = () => {
        apiCall(setLoading, appContext,
            () => adGroupApi.adGroupList(
                filters.sort?.name,
                filters.sort?.direction,
                filters.name,
                filters.active,
                filters.format,
                filters.advertiserId,
                filters.campaignId,
                filters.creativeId,
                filters.retargetingListId,
                {query: {page: filters.page, size: filters.pageSize}}
            ),
            (data) => setListData(data)
        )
    }

    const toggleActive = (id: number, checked: boolean) => {
        apiCall(setLoading, appContext,
            () => checked ? adGroupApi.massEnableAdGroup([id]) : adGroupApi.massDisableAdGroup([id]),
            () => {
                const adGroup = find(listData, id) as AdGroupBean
                adGroup.active = checked
                setListData(listData)
            }
        )
    }

    return (
        <ListCard loading={loading} title="Ad Groups" createName="Ad Group" createRoute={CommonRoutes.AD_GROUP_CREATE}
                  listRouteKey={CommonRoutes.AD_GROUP}
                  filters={filters} setFilters={setFilters}
                  listData={listData}
                  loadData={loadData}
                  columns={[
                      {
                          title: "ID",
                          dataIndex: "id",
                          width: COLUMN_ID_WIDTH,
                          fixed: 'left'
                      },
                      {
                          title: "Name",
                          dataIndex: "name",
                          className: "list-table-name",
                          sorter: true,
                          ellipsis: true,
                          render: (text, record) => (<Link style={{color: '#5888CF', minWidth: 100}}
                                                           to={`${CommonRoutes.AD_GROUP}/${record.id}/edit`}>{text}</Link>),
                          width: COLUMN_NAME_WIDTH,
                          fixed: 'left'
                      }, {
                          title: "Priority",
                          dataIndex: "priority",
                          sorter: true,
                          width: 80,
                          render: (priority: AdGroupBean.PriorityEnum | undefined) =>
                              priority ? (
                                  <span
                                      title={String(priority)}
                                      style={{
                                          width: 24,
                                          justifyContent: 'center',
                                          color: priorityColors[priority],
                                          fontSize: 16,
                                      }}
                                  >
                                      {renderPriorityIcon(priority)}
                                  </span>
                              ) : null
                      }, {
                          title: "Creat.",
                          dataIndex: "creatives",
                          width: 55,
                          render: (value, record) => (<Link to={`${CommonRoutes.CREATIVE}?adGroupId=${record.id}`}
                                                            style={{color: '#5888CF'}}>{value}</Link>)
                      },
                      ...(agency ? [{
                          title: "Advertiser",
                          dataIndex: "advertiserName",
                          sorter: true,
                          ellipsis: true,
                          width: 120
                      }] : []), {
                          title: "Campaign",
                          dataIndex: "campaignName",
                          sorter: true,
                          ellipsis: true,
                          width: 120
                      }, {
                          title: "Today cost/budget",
                          dataIndex: "dailyBudget",
                          sorter: true,
                          width: 120,
                          render: (_, record) => (
                              <>
                                  {formatCompact(Format.Cost, record.dailyCost)} /
                                  <span style={{color: '#9FA3A9'}}> {formatCompact(Format.Cost, record.dailyBudget)}</span>
                              </>
                          )
                      }, {
                          title: "Total cost/budget",
                          dataIndex: "totalBudget",
                          sorter: true,
                          width: 120,
                          render: (_, record) => (
                              <>
                                  {formatCompact(Format.Cost, record.totalCost)} /
                                  <span style={{color: '#9FA3A9'}}> {formatCompact(Format.Cost, record.totalBudget)}</span>
                              </>
                          )

                      },
                      {
                          title: 'Time Zone',
                          dataIndex: 'timezone',
                          sorter: false,
                          width: 60,
                      },
                      {
                          title: "Start Date",
                          dataIndex: "startDate",
                          sorter: true,
                          width: 80,
                          render: (date) => date && dayjs(date).format('DD MMM YY')
                      }, {
                          title: "End Date",
                          dataIndex: "endDate",
                          sorter: true,
                          width: 80,
                          render: (date) => date && dayjs(date).format('DD MMM YY')
                      }, {
                          title: "Days Left",
                          dataIndex: "daysLeft",
                          sorter: true,
                          width: 70
                      }, {
                          title: "Last Modified",
                          dataIndex: "modified",
                          sorter: true,
                          width: 100,
                          render: (date) => date && dayjs(date).format('DD MMM YY')
                      }, {
                          title: "Active",
                          dataIndex: "active",
                          sorter: true,
                          render: (val, record) => (
                              <Switch size="small" checked={record.active}
                                      onChange={(checked) => record.id && toggleActive(record.id, checked)}/>
                          ),
                          width: 60
                      }, {
                          title: "Actions",
                          render: (_, record: AdGroupBean) => (
                              <div style={{display: 'flex', gap: 8, justifyContent: 'center'}}>
                                  <ListActionButton onClick={() => navigateTo(`${CommonRoutes.AD_GROUP}/${record.id}/copy`)} tooltip='Duplicate'>
                                      <CopyOutlined/>
                                  </ListActionButton>
                                  <GoToReportButton title="Report" filterName="AdGroupId" id={record.id} />
                              </div>
                          ),
                          width: 100
                      }
                  ]}
        >
            <SearchInput value={filters.name} onChange={(value) => setFilter('name', value)}/>
            {agency && <AdvertiserSelector
                placeholder="Filter by Advertiser"
                style={{minWidth: '200px'}}
                value={filters.advertiserId}
                onChange={(value: number) => setFilter('advertiserId', value)}
            />}
            <CampaignSelector
                placeholder="Filter by Campaign"
                style={{minWidth: '200px'}}
                value={filters.campaignId}
                onChange={(value: number) => setFilter('campaignId', value)}
                advertiserId={filters.advertiserId}
            />
            <CreativeSelector
                placeholder="Filter by Creative"
                style={{minWidth: '200px'}}
                value={filters.creativeId}
                onChange={(value: number) => setFilter('creativeId', value)}
                advertiserId={filters.advertiserId}
            />
            <RetargetingListSelector
                placeholder="Filter by Retargeting List"
                style={{minWidth: '220px'}}
                value={filters.retargetingListId}
                onChange={(value: number) => setFilter('retargetingListId', value)}
                advertiserId={filters.advertiserId}
            />
            <ActiveStatusSelector
                placeholder="Filter by Active"
                style={{minWidth: '100px'}}
                value={filters.active}
                onChange={(value) => setFilter('active', value)}
            />
        </ListCard>
    )
}

export default AdGroupList;
